import * as math from './math';
import { compute } from './compute';
import { OHLCBar, VolumeBar } from './types';

/**
 * Calculates RSI for an array of data and returns a new array with RSI values
 * @param bars Array of objects
 * @param period RSI period (default: 14)
 * @param field Field to calculate RSI on (default: 'close')
 * @returns Array of objects with RSI values
 */
export const rsi = <T>(
    bars: T[],
    period: number = 14,
    field: keyof T = 'close' as keyof T
): Array<T & { [key: `rsi${string}`]: number | undefined }> => {
    // Create field name for the RSI property
    const fieldStr = String(field);
    const rsiFieldName = `rsi${fieldStr.charAt(0).toUpperCase() + fieldStr.slice(1)}`;

    // Pre-calculate gains and losses for all bars
    const values = bars.map(b => b[field] as any as number);
    const gains: number[] = [];
    const losses: number[] = [];
    
    // Calculate price changes
    for (let i = 0; i < values.length; i++) {
        if (i === 0) {
            gains[i] = 0;
            losses[i] = 0;
        } else {
            const change = values[i] - values[i - 1];
            gains[i] = change > 0 ? change : 0;
            losses[i] = change < 0 ? Math.abs(change) : 0;
        }
    }

    // Use compute to calculate RSI values
    return compute(
        bars,
        rsiFieldName as any,
        (bar, index, allBars, params) => {
            if (index < params.period - 1) {
                return undefined;
            }

            let avgGain: number;
            let avgLoss: number;

            if (index === params.period - 1) {
                // First RSI calculation: simple average
                let sumGain = 0;
                let sumLoss = 0;
                for (let i = 0; i < params.period; i++) {
                    sumGain += params.gains[i];
                    sumLoss += params.losses[i];
                }
                avgGain = sumGain / params.period;
                avgLoss = sumLoss / params.period;
            } else {
                // Subsequent RSI calculations: use Wilder's smoothing
                let sumGain = 0;
                let sumLoss = 0;
                
                // Initial average for first period
                for (let i = 0; i < params.period; i++) {
                    sumGain += params.gains[i];
                    sumLoss += params.losses[i];
                }
                avgGain = sumGain / params.period;
                avgLoss = sumLoss / params.period;
                
                // Apply Wilder's smoothing for subsequent values
                for (let i = params.period; i <= index; i++) {
                    avgGain = (avgGain * (params.period - 1) + params.gains[i]) / params.period;
                    avgLoss = (avgLoss * (params.period - 1) + params.losses[i]) / params.period;
                }
            }

            // Calculate RSI
            if (avgLoss === 0) {
                return 100; // If there are no losses, RSI is 100
            }
            
            const rs = avgGain / avgLoss;
            const rsiValue = 100 - (100 / (1 + rs));
            
            return rsiValue;
        },
        { period, field, gains, losses }
    );
};

/**
 * Calculates Simple Moving Average for an array of data
 * @param bars Array of objects
 * @param field Field to calculate SMA on
 * @param targetField Custom field name for the result
 * @param period SMA period (default: 20)
 * @returns Array of objects with SMA values
 */
export const sma = <T, K extends string>(
    bars: T[],
    field: keyof T,
    targetField: K,
    period: number = 20
): Array<T & { [P in K]: number | undefined }> => {
    // Use compute to calculate SMA values
    return compute(
        bars,
        targetField,
        (bar, index, allBars, params) => {
            if (index < params.period - 1) {
                return undefined;
            }

            // Calculate SMA directly
            const values = allBars.map(b => b[params.field] as any as number);
            const slice = values.slice(index - params.period + 1, index + 1);
            const sum = slice.reduce((acc, val) => acc + val, 0);
            return sum / params.period;
        },
        { period, field }
    );
};

/**
 * Calculates Exponential Moving Average for an array of data
 * @param bars Array of objects
 * @param field Field to calculate EMA on
 * @param targetField Custom field name for the result
 * @param period EMA period
 * @returns Array of objects with EMA values
 */
export const ema = <T, K extends string>(
    bars: T[],
    field: keyof T,
    targetField: K,
    period: number
): Array<T & { [P in K]: number | undefined }> => {
    return compute(
        bars,
        targetField,
        (bar, index, allBars, params) => {
            if (index < params.period - 1) {
                return undefined;
            }
            
            if (index === params.period - 1) {
                // Initial SMA
                const sum = allBars.slice(0, params.period)
                    .reduce((s, b) => s + (b[params.field] as any as number), 0);
                return sum / params.period;
            }
            
            // EMA calculation
            const k = 2 / (params.period + 1);
            const prevEMA = (allBars[index - 1] as any)[targetField];
            return (bar[params.field] as any as number) * k + prevEMA * (1 - k);
        },
        { period, field }
    );
};

/**
 * Calculates Bollinger Bands for an array of data
 * @param bars Array of objects
 * @param field Field to calculate Bollinger Bands on (default: 'close')
 * @param period Period for the moving average (default: 20)
 * @param stdDevMultiplier Standard deviation multiplier (default: 2)
 * @returns Array of objects with upper, middle, and lower band values
 */
export const bollingerBands = <T>(
    bars: T[],
    field: keyof T = 'close' as keyof T,
    period: number = 20,
    stdDevMultiplier: number = 2
): Array<T & {
    upperBand: number | undefined;
    middleBand: number | undefined;
    lowerBand: number | undefined;
    bbWidth: number | undefined;
    bbPercent: number | undefined;
}> => {
    // First, calculate the middle band (SMA)
    const barsWithMiddleBand = compute(
        bars,
        'middleBand',
        (bar, index, allBars, params) => {
            if (index < params.period - 1) {
                return undefined;
            }

            const slice = allBars.slice(index - params.period + 1, index + 1);
            return math.average(slice, params.field);
        },
        { period, field }
    );

    // Then calculate the upper band
    const barsWithUpperBand = compute(
        barsWithMiddleBand,
        'upperBand',
        (bar, index, allBars, params) => {
            if (index < params.period - 1 || bar.middleBand === undefined) {
                return undefined;
            }

            const slice = allBars.slice(index - params.period + 1, index + 1);
            const middleBand = bar.middleBand;

            // Calculate standard deviation
            const variance = slice.reduce((sum, b) => {
                const value = b[params.field] as any as number;
                return sum + Math.pow(value - middleBand, 2);
            }, 0) / params.period;

            const stdDev = Math.sqrt(variance);

            // Calculate upper band
            return middleBand + (stdDev * params.stdDevMultiplier);
        },
        { period, field, stdDevMultiplier }
    );

    // Then calculate the lower band
    const barsWithLowerBand = compute(
        barsWithUpperBand,
        'lowerBand',
        (bar, index, allBars, params) => {
            if (index < params.period - 1 || bar.middleBand === undefined) {
                return undefined;
            }

            const middleBand = bar.middleBand as number;
            const upperBand = bar.upperBand as number;

            // Calculate lower band based on the same standard deviation
            return 2 * middleBand - upperBand;
        },
        { period }
    );

    // Finally calculate the bandwidth and %B
    return compute(
        barsWithLowerBand,
        'bbMetrics',
        (bar, index, allBars, params) => {
            if (index < params.period - 1 ||
                bar.middleBand === undefined ||
                bar.upperBand === undefined ||
                bar.lowerBand === undefined) {
                return {
                    bbWidth: undefined,
                    bbPercent: undefined
                };
            }

            const middleBand = bar.middleBand as number;
            const upperBand = bar.upperBand as number;
            const lowerBand = bar.lowerBand as number;

            // Calculate bandwidth and %B
            const bbWidth = (upperBand - lowerBand) / middleBand;

            // %B shows where price is in relation to the bands (0-1)
            const currentValue = bar[params.field] as any as number;
            const bbPercent = (currentValue - lowerBand) / (upperBand - lowerBand);

            return {
                bbWidth,
                bbPercent
            };
        },
        { period, field }
    ) as unknown as Array<T & {
        upperBand: number | undefined;
        middleBand: number | undefined;
        lowerBand: number | undefined;
        bbWidth: number | undefined;
        bbPercent: number | undefined;
    }>;
};